import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  FormField,
  Select,
  Checkbox,
  Input,
  SpaceBetween,
  Box,
  Alert,
  ColumnLayout,
  Container,
  Header,
  Link,
} from '@amzn/awsui-components-console';
import type { SelectProps } from '@amzn/awsui-components-console';
import { useCronValidation } from '../../hooks/useCronValidation';
import './CronBuilder.scss';

/**
 * Props for the CronBuilder component
 */
export const CronBuilder: React.FC<CronBuilderProps> = ({
  value,
  onChange,
  onValidationChange,
}) => {
  const [state, setState] = useState<CronBuilderState>(() => {
    const initialState: CronBuilderState = {
      activeTab: 'daily',
      customExpression: value || '',
      selectedMinute: 0,
      selectedHour: 12,
      selectedDaysOfWeek: [2], // Monday
      selectedDayOfMonth: 1,
    };

    if (value && value.trim()) {
      // Try to detect if the value matches a preset tab
      const detected = detectTabFromCron(value.trim());
      initialState.activeTab = detected;
      if (detected === 'custom') {
        initialState.customExpression = value;
      }
    }

    return initialState;
  });

  const [showAdvancedPreview, setShowAdvancedPreview] = useState(false);

  const { isValid, error, nextExecutions, humanReadable } = useCronValidation({
    expression: value,
    timezone: 'UTC',
  });

  useEffect(() => {
    onValidationChange(isValid, error || undefined);
  }, [isValid, error, onValidationChange]);

  /**
   * Generates a cron expression based on the current state
   * AWS EventBridge format: minute hour day-of-month month day-of-week year
   */
  const generateCronExpression = useCallback((currentState: CronBuilderState): string => {
    const { activeTab, selectedMinute, selectedHour, selectedDaysOfWeek, selectedDayOfMonth, customExpression } = currentState;

    switch (activeTab) {
      case 'daily':
        return `${selectedMinute} ${selectedHour} * * ? *`;

      case 'weekly':
        if (selectedDaysOfWeek.length === 0) {
          return `${selectedMinute} ${selectedHour} ? * * *`;
        }
        const daysStr = selectedDaysOfWeek.sort((a, b) => a - b).join(',');
        return `${selectedMinute} ${selectedHour} ? * ${daysStr} *`;

      case 'monthly':
        return `${selectedMinute} ${selectedHour} ${selectedDayOfMonth} * ? *`;

      case 'custom':
        return customExpression;

      default:
        return '';
    }
  }, []);

  const updateState = useCallback((updates: Partial<CronBuilderState>) => {
    setState((prevState) => {
      const newState = { ...prevState, ...updates };
      const newExpression = generateCronExpression(newState);
      onChange(newExpression);
      return newState;
    });
  }, [generateCronExpression, onChange]);

  const handleTabChange = useCallback((tabId: string) => {
    updateState({ activeTab: tabId as CronBuilderState['activeTab'] });
  }, [updateState]);

  const handleDayOfWeekChange = useCallback((dayValue: number, checked: boolean) => {
    setState((prevState) => {
      const newDays = checked
        ? [...prevState.selectedDaysOfWeek, dayValue]
        : prevState.selectedDaysOfWeek.filter((d) => d !== dayValue);

      const newState = { ...prevState, selectedDaysOfWeek: newDays };
      const newExpression = generateCronExpression(newState);
      onChange(newExpression);
      return newState;
    });
  }, [generateCronExpression, onChange]);

  const renderDailyTab = () => (
    <Box padding="m">
      <SpaceBetween size="m">
        <ColumnLayout columns={2}>
          <FormField label="Hour (UTC)">
            <Select
              selectedOption={HOUR_OPTIONS.find((opt) => opt.value === String(state.selectedHour)) || null}
              onChange={({ detail }) => updateState({ selectedHour: parseInt(detail.selectedOption.value || '12', 10) })}
              options={HOUR_OPTIONS}
              placeholder="Select hour"
            />
          </FormField>
          <FormField label="Minute">
            <Select
              selectedOption={MINUTE_OPTIONS.find((opt) => opt.value === String(state.selectedMinute)) || null}
              onChange={({ detail }) => updateState({ selectedMinute: parseInt(detail.selectedOption.value || '0', 10) })}
              options={MINUTE_OPTIONS}
              placeholder="Select minute"
            />
          </FormField>
        </ColumnLayout>
        <Box color="text-body-secondary">
          The schedule will run every day at {state.selectedHour.toString().padStart(2, '0')}:{state.selectedMinute.toString().padStart(2, '0')} UTC.
        </Box>
      </SpaceBetween>
    </Box>
  );

  const renderWeeklyTab = () => (
    <Box padding="m">
      <SpaceBetween size="m">
        <FormField label="Days of week">
          <SpaceBetween size="xs" direction="horizontal">
            {DAYS_OF_WEEK.map((day) => (
              <Checkbox
                key={day.value}
                checked={state.selectedDaysOfWeek.includes(day.value)}
                onChange={({ detail }) => handleDayOfWeekChange(day.value, detail.checked)}
              >
                {day.short}
              </Checkbox>
            ))}
          </SpaceBetween>
        </FormField>
        <ColumnLayout columns={2}>
          <FormField label="Hour (UTC)">
            <Select
              selectedOption={HOUR_OPTIONS.find((opt) => opt.value === String(state.selectedHour)) || null}
              onChange={({ detail }) => updateState({ selectedHour: parseInt(detail.selectedOption.value || '12', 10) })}
              options={HOUR_OPTIONS}
              placeholder="Select hour"
            />
          </FormField>
          <FormField label="Minute">
            <Select
              selectedOption={MINUTE_OPTIONS.find((opt) => opt.value === String(state.selectedMinute)) || null}
              onChange={({ detail }) => updateState({ selectedMinute: parseInt(detail.selectedOption.value || '0', 10) })}
              options={MINUTE_OPTIONS}
              placeholder="Select minute"
            />
          </FormField>
        </ColumnLayout>
        {state.selectedDaysOfWeek.length === 0 && (
          <Alert type="warning">
            Please select at least one day of the week.
          </Alert>
        )}
      </SpaceBetween>
    </Box>
  );

  const renderMonthlyTab = () => (
    <Box padding="m">
      <SpaceBetween size="m">
        <FormField label="Day of month">
          <Select
            selectedOption={DAY_OF_MONTH_OPTIONS.find((opt) => opt.value === String(state.selectedDayOfMonth)) || null}
            onChange={({ detail }) => updateState({ selectedDayOfMonth: parseInt(detail.selectedOption.value || '1', 10) })}
            options={DAY_OF_MONTH_OPTIONS}
            placeholder="Select day"
          />
        </FormField>
        <ColumnLayout columns={2}>
          <FormField label="Hour (UTC)">
            <Select
              selectedOption={HOUR_OPTIONS.find((opt) => opt.value === String(state.selectedHour)) || null}
              onChange={({ detail }) => updateState({ selectedHour: parseInt(detail.selectedOption.value || '12', 10) })}
              options={HOUR_OPTIONS}
              placeholder="Select hour"
            />
          </FormField>
          <FormField label="Minute">
            <Select
              selectedOption={MINUTE_OPTIONS.find((opt) => opt.value === String(state.selectedMinute)) || null}
              onChange={({ detail }) => updateState({ selectedMinute: parseInt(detail.selectedOption.value || '0', 10) })}
              options={MINUTE_OPTIONS}
              placeholder="Select minute"
            />
          </FormField>
        </ColumnLayout>
        <Box color="text-body-secondary">
          The schedule will run on day {state.selectedDayOfMonth} of every month at {state.selectedHour.toString().padStart(2, '0')}:{state.selectedMinute.toString().padStart(2, '0')} UTC.
        </Box>
      </SpaceBetween>
    </Box>
  );

  const renderAdvancedTab = () => (
    <Box padding="m">
      <SpaceBetween size="m">
        <FormField
          label="Cron expression"
          description="AWS EventBridge cron format: minute hour day-of-month month day-of-week year"
          errorText={!isValid && state.activeTab === 'custom' ? error : undefined}
        >
          <Input
            value={state.customExpression}
            onChange={({ detail }) => {
              setState((prev) => ({ ...prev, customExpression: detail.value }));
              onChange(detail.value);
            }}
            placeholder="0 12 * * ? *"
          />
        </FormField>
        <Alert type="info">
          <SpaceBetween size="xs">
            <Box fontWeight="bold">Cron expression format:</Box>
            <Box>
              <code>minute hour day-of-month month day-of-week year</code>
            </Box>
            <Box>
              Examples:
              <ul>
                <li><code>0 12 * * ? *</code> - Every day at 12:00 UTC</li>
                <li><code>0 8 ? * MON-FRI *</code> - Weekdays at 8:00 UTC</li>
                <li><code>0 0 1 * ? *</code> - First day of every month at midnight</li>
              </ul>
            </Box>
            <Box color="text-status-warning">
              Note: Schedules that run more frequently than once per day (e.g., every minute or every hour) are not allowed.
            </Box>
          </SpaceBetween>
        </Alert>
      </SpaceBetween>
    </Box>
  );

  // Tab definitions - Daily, Weekly, Monthly presets + Advanced for power users
  const tabs = [
    { id: 'daily', label: 'Daily', content: renderDailyTab() },
    { id: 'weekly', label: 'Weekly', content: renderWeeklyTab() },
    { id: 'monthly', label: 'Monthly', content: renderMonthlyTab() },
    { id: 'custom', label: 'Advanced', content: renderAdvancedTab() },
  ];

  return (
    <div className="cron-builder">
      <SpaceBetween size="l">
        <Tabs
          activeTabId={state.activeTab}
          onChange={({ detail }) => handleTabChange(detail.activeTabId)}
          tabs={tabs}
        />

        {!isValid && error && (
          <Alert type="error" header="Invalid schedule expression">
            {error}
          </Alert>
        )}

        {isValid && (
          <Container header={<Header variant="h3">Schedule Preview</Header>}>
            <SpaceBetween size="m">
              {humanReadable && (
                <Box>
                  <Box fontWeight="bold" color="text-label">Runs</Box>
                  <Box fontSize="heading-m">{humanReadable}</Box>
                </Box>
              )}
              {nextExecutions.length > 0 && (
                <Box>
                  <Box fontWeight="bold" color="text-label">Upcoming executions (UTC)</Box>
                  <SpaceBetween size="xs">
                    {nextExecutions.map((date, index) => (
                      <Box key={index} color="text-body-secondary">
                        {formatExecutionTime(date)}
                      </Box>
                    ))}
                  </SpaceBetween>
                </Box>
              )}
              <Box>
                <Link
                  onFollow={(e) => { e.preventDefault(); setShowAdvancedPreview(!showAdvancedPreview); }}
                  variant="secondary"
                  fontSize="body-s"
                >
                  {showAdvancedPreview ? 'Hide technical details' : 'Show technical details'}
                </Link>
                {showAdvancedPreview && (
                  <Box margin={{ top: 'xs' }}>
                    <Box fontWeight="bold" color="text-label" fontSize="body-s">Cron Expression</Box>
                    <Box fontSize="body-s"><code>{value}</code></Box>
                  </Box>
                )}
              </Box>
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </div>
  );
}


// Day of week options (1=Sunday, 7=Saturday in EventBridge)
const DAYS_OF_WEEK = [
  { value: 1, label: 'Sunday', short: 'SUN' },
  { value: 2, label: 'Monday', short: 'MON' },
  { value: 3, label: 'Tuesday', short: 'TUE' },
  { value: 4, label: 'Wednesday', short: 'WED' },
  { value: 5, label: 'Thursday', short: 'THU' },
  { value: 6, label: 'Friday', short: 'FRI' },
  { value: 7, label: 'Saturday', short: 'SAT' },
];

// Generate hour options (0-23)
const HOUR_OPTIONS: SelectProps.Option[] = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: i.toString().padStart(2, '0') + ':00',
}));

// Generate minute options (0-59)
const MINUTE_OPTIONS: SelectProps.Option[] = Array.from({ length: 60 }, (_, i) => ({
  value: String(i),
  label: i.toString().padStart(2, '0'),
}));

// Generate day of month options (1-31)
const DAY_OF_MONTH_OPTIONS: SelectProps.Option[] = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

/**
 * Formats a Date object to a human-readable string
 */
const formatExecutionTime = (date: Date): string => {
  return date.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
};

/**
 * Detect which preset tab matches a given cron expression.
 * Returns 'custom' if no preset matches.
 */
const detectTabFromCron = (cron: string): CronBuilderState['activeTab'] => {
  const parts = cron.split(/\s+/);
  if (parts.length !== 6) return 'custom';
  const [, , dom, month, dow, year] = parts;
  // Daily: * * ? * (any day-of-month, any month, ? day-of-week)
  if (dom === '*' && month === '*' && dow === '?' && year === '*') return 'daily';
  // Weekly: ? * <days> * (? day-of-month, specific days-of-week)
  if (dom === '?' && month === '*' && dow !== '*' && dow !== '?' && year === '*') return 'weekly';
  // Monthly: <day> * ? * (specific day-of-month, ? day-of-week)
  if (dom !== '*' && dom !== '?' && month === '*' && dow === '?' && year === '*') return 'monthly';
  return 'custom';
};

export default CronBuilder;
